<template>
  <HeaderBar />

  <section class="carvia-container">
    <div class="mb-4 flex flex-col gap-4">
      <div class="grid grid-cols-3 gap-4">
        <Input v-model="searchInput" :placeholder="t('searchPlaceholder')" variant="ghost" />
        <DateRangeFilterPicker
          v-model:start-date="dataDateRangeStart"
          v-model:end-date="dataDateRangeEnd"
          :placeholder="t('dataDateRangePlaceholder')"
          class="w-full"
        />
        <CVButton @click.prevent="showUploadModal = true"
          ><UploadIcon class="size-4" /> {{ t('uploadInfringements') }}</CVButton
        >
      </div>
      <Tabs v-model="listTab" :tabs="listTabs" class="min-w-0 max-w-full" />
    </div>

    <div v-if="!isLoading" class="space-y-2">
      <InfringementListItem
        v-for="infringement in infringements"
        :key="infringement.id"
        :infringement="infringement"
      />
    </div>

    <EntityListExceptions
      :status="status"
      :error="error"
      :items-count="infringements.length"
      :no-items-text="t('noInfringementsFound')"
    />

    <div v-if="hasNextPage" class="border-primary/30 mt-4 flex justify-center border-t pt-6">
      <CVButton outline :is-loading="isFetchingNextPage" @click="fetchNextPage">
        {{ t('loadMore') }}
      </CVButton>
    </div>
  </section>

  <InfringementUploadModal v-model:show="showUploadModal" />
</template>

<script lang="ts" setup>
import { computed, ref, watch, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { refDebounced } from '@vueuse/core';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Input from '@/components/Input.vue';
import DateRangeFilterPicker from '@/components/DateRangeFilterPicker.vue';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import Tabs, { type TabItem } from '@/components/Tabs.vue';
import InfringementListItem from './InfringementListItem.vue';
import InfringementUploadModal from '../infringement-create/InfringementUploadModal.vue';
import {
  INFRINGEMENTS_QUERY_KEY,
  INFRINGEMENT_USER_ACTION_NEEDED_COUNT_QUERY_KEY,
  useInfringements,
  useInfringementUserActionNeededCount,
} from '@/queries/use-infringements';
import { useInfringementStatusEvents } from '@/queries/use-infringement-status-events';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useUiStore } from '@/stores/ui.store';
import { useQueryClient } from '@tanstack/vue-query';
import { Order } from '@/entities/pagination/order.enum';
import { addItemToQueriesData, updateItemOnQueriesData } from '@/utils/update-query-data.ts';
import {
  FindAllInfringementsSort,
  type InfringementsControllerFindAllData,
  InfringementStatus,
} from '@carvia/ros-client-types';
import { endOfDay, startOfDay } from 'date-fns';
import { UploadIcon } from 'lucide-vue-next';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';

const enum ListTab {
  ALL = 'ALL',
  AWAITING_CONFIRMATION = 'AWAITING_CONFIRMATION',
  DATA_COLLECTION_FAILED = 'DATA_COLLECTION_FAILED',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  OTHER_EXECUTION_FAILED = 'OTHER_EXECUTION_FAILED',
}

const { t } = useI18n();
const uiStore = useUiStore();
const queryClient = useQueryClient();
const listTab = ref<string>(ListTab.ALL);
const showUploadModal = ref(false);
const searchInput = useRouteQueryRef('search', '', 'string');
const debouncedSearch = refDebounced(searchInput, 400);
const dataDateRangeStart = useRouteQueryRef('dateFrom', null, 'date');
const dataDateRangeEnd = useRouteQueryRef('dateTo', null, 'date');

const infringementsBaseQuery = computed((): InfringementsControllerFindAllData['query'] => {
  const trimmedSearch = debouncedSearch.value.trim();
  return {
    search: trimmedSearch || undefined,
    dateFrom: dataDateRangeStart.value ? startOfDay(dataDateRangeStart.value) : undefined,
    dateTo: dataDateRangeEnd.value ? endOfDay(dataDateRangeEnd.value) : undefined,
  };
});

const infringementsQuery = computed((): InfringementsControllerFindAllData['query'] => {
  const baseQuery = {
    ...infringementsBaseQuery.value,
    limit: 20,
    sort: [FindAllInfringementsSort.CREATED_DATE],
    order: Order.DESC,
  };
  switch (listTab.value) {
    case ListTab.AWAITING_CONFIRMATION:
      return {
        ...baseQuery,
        statuses: [InfringementStatus.AWAITING_USER_CONFIRMATION],
      };
    case ListTab.DATA_COLLECTION_FAILED:
      return {
        ...baseQuery,
        statuses: [
          InfringementStatus.EXTRACTING_DATA,
          InfringementStatus.AWAITING_USER_CONFIRMATION,
        ],
        hasSomeErrorsOf: ['extractData', 'checkDuplicateFileNumber', 'findBooking'],
      };
    case ListTab.PAYMENT_FAILED:
      return {
        ...baseQuery,
        statuses: [InfringementStatus.EXECUTING],
        hasSomeErrorsOf: ['chargeFee', 'sendPaymentLink'],
      };
    case ListTab.OTHER_EXECUTION_FAILED:
      return {
        ...baseQuery,
        statuses: [InfringementStatus.EXECUTING],
        hasSomeErrorsOf: ['sendInvoice', 'sendAuthorityEmail'],
      };
    default:
      return baseQuery;
  }
});

const { data: userActionNeededCountData } =
  useInfringementUserActionNeededCount(infringementsBaseQuery);

const listTabs = computed<TabItem[]>(() => {
  const c = userActionNeededCountData.value;
  const tabBadge = (n?: number) =>
    typeof n === 'number' && n > 0 ? String(n) + (n > 99 ? '+' : '') : undefined;
  return [
    { id: ListTab.ALL, title: t('tabAll') },
    {
      id: ListTab.AWAITING_CONFIRMATION,
      title: t('tabAwaitingConfirmation'),
      badge: tabBadge(c?.awaitingConfirmationCount),
    },
    {
      id: ListTab.DATA_COLLECTION_FAILED,
      title: t('tabDataCollectionFailed'),
      badge: tabBadge(c?.dataCollectionFailedCount),
    },
    {
      id: ListTab.PAYMENT_FAILED,
      title: t('tabPaymentFailed'),
      badge: tabBadge(c?.paymentFailedCount),
    },
    {
      id: ListTab.OTHER_EXECUTION_FAILED,
      title: t('tabOtherExecutionFailed'),
      badge: tabBadge(c?.otherExecutionFailedCount),
    },
  ];
});

const {
  data: infringementsData,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  error,
  status,
} = useInfringements(infringementsQuery);

const infringements = useFlattenPaginatedData(infringementsData);

const { events } = useInfringementStatusEvents();
const processedEventsCount = ref(0);

watch(
  events,
  (newEvents) => {
    if (newEvents.length <= processedEventsCount.value) {
      return;
    }

    const newUnprocessedEvents = newEvents.slice(processedEventsCount.value);
    newUnprocessedEvents.forEach((event) => {
      if (event.type === 'create') {
        addItemToQueriesData([INFRINGEMENTS_QUERY_KEY], event.infringement, queryClient);
      } else {
        updateItemOnQueriesData([INFRINGEMENTS_QUERY_KEY], event.infringement, queryClient);
      }
    });
    void queryClient.invalidateQueries({
      queryKey: [INFRINGEMENT_USER_ACTION_NEEDED_COUNT_QUERY_KEY],
    });
    processedEventsCount.value = newEvents.length;
  },
  { deep: true },
);

watchEffect(() => {
  uiStore.setHeaderTitle(t('infringements'));
});
</script>

<i18n lang="json">
{
  "en": {
    "infringements": "Infringements",
    "noInfringementsFound": "No infringements found",
    "loadMore": "Load more",
    "uploadInfringements": "Upload Infringements",
    "tabAll": "All",
    "tabAwaitingConfirmation": "Awaiting confirmation",
    "tabDataCollectionFailed": "Data collection failed",
    "tabPaymentFailed": "Payment failed",
    "tabOtherExecutionFailed": "Other",
    "searchPlaceholder": "Search",
    "dataDateRangePlaceholder": "Infringement date (from – to)"
  },
  "de": {
    "infringements": "Ordnungswidrigkeiten",
    "noInfringementsFound": "Keine Ordnungswidrigkeiten gefunden",
    "loadMore": "Mehr laden",
    "uploadInfringements": "Ordnungswidrigkeiten hochladen",
    "tabAll": "Alle",
    "tabAwaitingConfirmation": "Warte auf Bestätigung",
    "tabDataCollectionFailed": "Daten sammeln fehlgeschlagen",
    "tabPaymentFailed": "Zahlung fehlgeschlagen",
    "tabOtherExecutionFailed": "Sonstiges",
    "searchPlaceholder": "Suche",
    "dataDateRangePlaceholder": "OWI Zeitraum"
  },
  "es": {
    "infringements": "Infracciones administrativas",
    "noInfringementsFound": "No se han encontrado infracciones",
    "loadMore": "Cargar más",
    "uploadInfringements": "Subir infracciones",
    "tabAll": "Todas",
    "tabAwaitingConfirmation": "Pendientes de confirmación",
    "tabDataCollectionFailed": "Recopilación de datos fallida",
    "tabPaymentFailed": "Pago fallido",
    "tabOtherExecutionFailed": "Otros",
    "searchPlaceholder": "Buscar",
    "dataDateRangePlaceholder": "Fecha de infracción (desde – hasta)"
  }
}
</i18n>
